import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, ArrowLeft, UserX } from 'lucide-react';
import SubscriptionBanner from '@/components/SubscriptionBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { userId } = useParams();
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/profile`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setProfile(response.data);
      setLoading(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load profile';
      toast.error(message);
      navigate('/friends');
    }
  };

  const handleBlock = async () => {
    if (!window.confirm('Block this user? They will be removed from your friends list.')) return;

    try {
      await axios.post(
        `${API}/blocks`,
        { blocked_user_id: userId },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      toast.success('User blocked');
      navigate('/friends');
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-bg">
      <SubscriptionBanner />
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          className="mb-6 text-gray-400 hover:text-white hover:bg-white/5"
          onClick={() => navigate('/friends')}
          data-testid="back-to-friends"
        >
          <ArrowLeft size={18} className="mr-2" /> Back to Friends
        </Button>

        <div className="dark-card p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-4xl heading-font font-bold tracking-tight mb-2 text-white" data-testid="profile-name">
                {profile.name}
              </h1>
              <p className="text-gray-400 body-font">{profile.email}</p>
              <div className="flex items-center gap-4 mt-4">
                <span className="text-sm px-3 py-1 bg-white/5 border border-white/10 rounded-xl body-font text-gray-300">
                  {profile.profile_visibility === 'public' && 'Public Profile'}
                  {profile.profile_visibility === 'friends' && 'Friends Only'}
                  {profile.profile_visibility === 'private' && 'Private Profile'}
                </span>
                {profile.is_friend && (
                  <span className="text-sm px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-xl body-font">
                    Friend
                  </span>
                )}
              </div>
            </div>
            {userId !== auth.user.id && (
              <Button
                variant="outline"
                className="text-red-400 border-red-400/30 hover:bg-red-500/10 hover:border-red-400"
                onClick={handleBlock}
                data-testid="block-user-btn"
              >
                <UserX size={18} className="mr-2" /> Block User
              </Button>
            )}
          </div>
        </div>

        {/* Scheduled Tasks */}
        <div className="dark-card p-6">
          <h2 className="text-2xl heading-font font-bold mb-6 text-white" data-testid="scheduled-tasks-title">
            <Calendar size={24} className="inline mr-2 text-teal-400" />
            Scheduled Tasks
          </h2>

          {profile.scheduled_tasks.length === 0 ? (
            <p className="text-center text-gray-400 py-8 body-font" data-testid="no-tasks">
              No scheduled tasks to display
            </p>
          ) : (
            <div className="space-y-3">
              {profile.scheduled_tasks.map((task) => (
                <div
                  key={task.id}
                  className="p-4 border border-white/10 rounded-xl"
                  data-testid={`task-${task.id}`}
                >
                  <h3 className="font-semibold body-font mb-2 text-white">{task.title}</h3>
                  {task.description && (
                    <p className="text-sm text-gray-400 mb-2 body-font">{task.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="label-uppercase">
                      {new Date(task.scheduled_time).toLocaleString()}
                    </span>
                    <span className="label-uppercase">{task.duration} min</span>
                    <span className="label-uppercase capitalize">{task.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;